import { Plan, FetchFrequency } from '../../types/registration'
import type { Step2Data } from '../../types/registration'

interface Step2BusinessProfileProps {
  data: Step2Data
  onChange: (data: Step2Data) => void
  errors: Partial<Record<keyof Step2Data, string>>
  onNext: () => void
  onBack: () => void
}

const INDUSTRY_OPTIONS = [
  'Food & Beverage',
  'Retail',
  'Healthcare',
  'Professional Services',
  'Hospitality',
  'Automotive',
  'Beauty & Wellness',
  'Other',
]

const PLAN_OPTIONS = [
  {
    value: Plan.STARTER,
    label: 'Starter',
    price: '$19.99/mo',
    description: 'Perfect for single-location businesses',
    locations: '1 location',
    features: 'Unified inbox + AI responses',
    badge: null,
  },
  {
    value: Plan.PRO,
    label: 'Growth',
    price: '$49.99/mo',
    description: 'For businesses managing multiple locations',
    locations: 'Up to 3 locations',
    features: 'Everything in Starter + Business intelligence',
    badge: 'Most Popular',
  },
  {
    value: Plan.AGENCY,
    label: 'Agency',
    price: '$99.99/mo',
    description: 'For agencies managing multiple clients',
    locations: 'Up to 5 locations',
    features: 'Everything in Growth + White-label + API',
    badge: null,
  },
]

const FETCH_FREQUENCY_OPTIONS = [
  { value: FetchFrequency.EVERY_4_HOURS, label: 'Every 4 hours' },
  { value: FetchFrequency.EVERY_12_HOURS, label: 'Every 12 hours' },
  { value: FetchFrequency.DAILY, label: 'Daily' },
  { value: FetchFrequency.MANUAL, label: 'Manual only' },
]

const BRAND_VOICE_MAX = 500

export default function Step2BusinessProfile({
  data,
  onChange,
  errors,
  onNext,
  onBack,
}: Step2BusinessProfileProps) {
  const handleChange =
    (field: keyof Step2Data) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      onChange({ ...data, [field]: e.target.value })
    }

  const handlePlanSelect = (plan: Plan) => {
    onChange({ ...data, plan })
  }

  const handleFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...data, fetch_frequency: e.target.value as FetchFrequency })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onNext()
  }

  const brandVoiceLength = data.brand_voice.length

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {/* Business Name */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <label htmlFor="business_name" className="text-sm font-medium text-rd-muted">
            Business Name <span aria-hidden="true" className="text-red-400">*</span>
          </label>
          <span className="relative group" aria-hidden="true">
            <svg className="w-3.5 h-3.5 text-rd-dim cursor-help" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 rounded-lg bg-rd-surface2 border border-white/10 px-3 py-2 text-xs text-rd-muted shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
              Enter your official brand name exactly as it appears on Google Maps (e.g. "Panera Bread", "Jane's Bakery"). This is used to find your business listing when fetching reviews.
            </span>
          </span>
        </div>
        <input
          id="business_name"
          type="text"
          autoComplete="organization"
          value={data.business_name}
          onChange={handleChange('business_name')}
          aria-required="true"
          aria-describedby={errors.business_name ? 'business_name-error' : undefined}
          aria-invalid={!!errors.business_name}
          className={`reg-input ${errors.business_name ? 'border-red-500/60' : ''}`}
          placeholder="Jane's Bakery"
        />
        {errors.business_name && (
          <p id="business_name-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.business_name}
          </p>
        )}
      </div>

      {/* Industry */}
      <div>
        <label htmlFor="industry" className="block text-sm font-medium text-rd-muted mb-1">
          Industry <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <select
          id="industry"
          value={data.industry}
          onChange={handleChange('industry')}
          aria-required="true"
          aria-describedby={errors.industry ? 'industry-error' : undefined}
          aria-invalid={!!errors.industry}
          className={`reg-input ${errors.industry ? 'border-red-500/60' : ''}`}
          style={{ colorScheme: 'dark' }}
        >
          <option value="" style={{ background: '#12121a' }}>Select an industry</option>
          {INDUSTRY_OPTIONS.map((opt) => (
            <option key={opt} value={opt} style={{ background: '#12121a' }}>
              {opt}
            </option>
          ))}
        </select>
        {errors.industry && (
          <p id="industry-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.industry}
          </p>
        )}
      </div>

      {/* Brand Voice */}
      <div>
        <div className="flex justify-between items-baseline mb-1">
          <label htmlFor="brand_voice" className="block text-sm font-medium text-rd-muted">
            Brand Voice <span aria-hidden="true" className="text-red-400">*</span>
          </label>
          <span
            aria-live="polite"
            aria-label={`${brandVoiceLength} of ${BRAND_VOICE_MAX} characters used`}
            className={`text-xs ${brandVoiceLength > BRAND_VOICE_MAX ? 'text-red-400 font-medium' : 'text-rd-dim'}`}
          >
            {brandVoiceLength}/{BRAND_VOICE_MAX}
          </span>
        </div>
        <textarea
          id="brand_voice"
          rows={3}
          maxLength={BRAND_VOICE_MAX}
          value={data.brand_voice}
          onChange={handleChange('brand_voice')}
          aria-required="true"
          aria-describedby={`brand_voice-hint${errors.brand_voice ? ' brand_voice-error' : ''}`}
          aria-invalid={!!errors.brand_voice}
          className={`reg-input resize-none ${errors.brand_voice ? 'border-red-500/60' : ''}`}
          placeholder="friendly, professional, empathetic"
        />
        <p id="brand_voice-hint" className="mt-1 text-xs text-rd-dim">
          Describe the tone AI should use when generating review responses.
        </p>
        {errors.brand_voice && (
          <p id="brand_voice-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.brand_voice}
          </p>
        )}
      </div>

      {/* Website URL (optional) */}
      <div>
        <label htmlFor="website_url" className="block text-sm font-medium text-rd-muted mb-1">
          Website URL <span className="text-rd-dim font-normal">(optional)</span>
        </label>
        <input
          id="website_url"
          type="url"
          autoComplete="url"
          value={data.website_url ?? ''}
          onChange={handleChange('website_url')}
          aria-describedby={errors.website_url ? 'website_url-error' : undefined}
          aria-invalid={!!errors.website_url}
          className={`reg-input ${errors.website_url ? 'border-red-500/60' : ''}`}
          placeholder="https://yourbusiness.com"
        />
        {errors.website_url && (
          <p id="website_url-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.website_url}
          </p>
        )}
      </div>

      {/* Plan Selection */}
      <fieldset>
        <legend className="block text-sm font-medium text-rd-muted mb-2">
          Plan <span aria-hidden="true" className="text-red-400">*</span>
        </legend>
        <div
          role="group"
          aria-describedby={errors.plan ? 'plan-error' : undefined}
          className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        >
          {PLAN_OPTIONS.map((plan) => {
            const isSelected = data.plan === plan.value
            return (
              <button
                key={plan.value}
                type="button"
                onClick={() => handlePlanSelect(plan.value)}
                aria-pressed={isSelected}
                className={`relative rounded-xl border-2 p-4 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-rd-accent/40 ${
                  isSelected
                    ? 'border-rd-accent/40 bg-[rgba(74,222,128,0.04)]'
                    : 'border-white/[0.07] bg-rd-bg hover:border-white/20'
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-rd-accent px-2 py-0.5 text-xs font-semibold text-rd-bg whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                <p className={`text-sm font-semibold ${isSelected ? 'text-rd-accent' : 'text-rd-text'}`}>
                  {plan.label}
                </p>
                <p className="text-lg font-bold mt-0.5 text-rd-text">
                  {plan.price}
                </p>
                <ul className="mt-2 space-y-0.5">
                  <li className="text-xs text-rd-muted">{plan.locations}</li>
                  <li className="text-xs text-rd-dim mt-1">{plan.features}</li>
                </ul>
              </button>
            )
          })}
        </div>
        {errors.plan && (
          <p id="plan-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.plan}
          </p>
        )}
        <p className="mt-2 text-xs text-rd-dim text-center">
          All plans include a 14-day free trial. No credit card required to start.
        </p>
      </fieldset>

      {/* Fetch Frequency */}
      <fieldset>
        <legend className="block text-sm font-medium text-rd-muted mb-2">
          Review Fetch Frequency <span aria-hidden="true" className="text-red-400">*</span>
        </legend>
        <div
          className="space-y-2"
          aria-describedby={errors.fetch_frequency ? 'fetch_frequency-error' : undefined}
        >
          {FETCH_FREQUENCY_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-3 cursor-pointer"
            >
              <input
                type="radio"
                name="fetch_frequency"
                value={opt.value}
                checked={data.fetch_frequency === opt.value}
                onChange={handleFrequencyChange}
                aria-required="true"
                className="h-4 w-4 accent-rd-accent border-white/20 focus:ring-rd-accent/40"
              />
              <span className="text-sm text-rd-muted">{opt.label}</span>
            </label>
          ))}
        </div>
        {data.fetch_frequency === FetchFrequency.MANUAL && (
          <div
            role="note"
            className="mt-3 flex items-start gap-2 rounded-lg bg-[rgba(34,211,238,0.06)] border border-[rgba(34,211,238,0.2)] px-3 py-2"
          >
            <svg
              aria-hidden="true"
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-rd-accent2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-xs text-rd-muted">
              Reviews will only be fetched when you click "Get My Reviews" on the dashboard.
            </p>
          </div>
        )}
        {errors.fetch_frequency && (
          <p id="fetch_frequency-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.fetch_frequency}
          </p>
        )}
      </fieldset>

      {/* Navigation Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="reg-btn-secondary flex-1"
        >
          Back
        </button>
        <button type="submit" className="reg-btn-primary flex-1">
          Next
        </button>
      </div>
    </form>
  )
}
